import { useCallback } from 'react';

import { create } from 'zustand';

import type {
  RecentsStorageV2,
  SavedGroupsStorageV2,
} from '@/lib/recoil/property-picker-storage-migration';

import {
  loadFavoriteProperties,
  loadPropertyPickerGroups,
  loadPropertyPickerRecents,
  persistFavoriteProperties,
  persistPropertyPickerGroups,
  persistPropertyPickerRecents,
} from './persist';

export type SetStateAction<T> = T | ((prev: T) => T);

function resolveUpdater<T>(prev: T, updater: SetStateAction<T>): T {
  return typeof updater === 'function'
    ? (updater as (prev: T) => T)(prev)
    : updater;
}

export type AppClientState = {
  greeting: string;
  pageBanner: boolean;
  recentPropertySelections: RecentsStorageV2;
  userPropertyGroups: SavedGroupsStorageV2;
  favoriteProperties: string[];
};

const initialAppClientState = (): AppClientState => ({
  greeting: '',
  pageBanner: false,
  recentPropertySelections: loadPropertyPickerRecents<RecentsStorageV2>({}),
  userPropertyGroups: loadPropertyPickerGroups<SavedGroupsStorageV2>({}),
  favoriteProperties: loadFavoriteProperties(),
});

export const useAppClientStore = create<AppClientState>(() =>
  initialAppClientState()
);

useAppClientStore.subscribe((state, prev) => {
  if (state.recentPropertySelections !== prev.recentPropertySelections) {
    persistPropertyPickerRecents(state.recentPropertySelections);
  }
  if (state.userPropertyGroups !== prev.userPropertyGroups) {
    persistPropertyPickerGroups(state.userPropertyGroups);
  }
  if (state.favoriteProperties !== prev.favoriteProperties) {
    persistFavoriteProperties(state.favoriteProperties);
  }
});

export function resetAppClientStore(): void {
  useAppClientStore.setState(initialAppClientState());
}

export function useGreetingState(): [
  string,
  (updater: SetStateAction<string>) => void,
] {
  const greeting = useAppClientStore((s) => s.greeting);
  const setGreeting = useCallback((updater: SetStateAction<string>) => {
    useAppClientStore.setState((state) => ({
      greeting: resolveUpdater(state.greeting, updater),
    }));
  }, []);
  return [greeting, setGreeting];
}

export function usePageBannerState(): [
  boolean,
  (updater: SetStateAction<boolean>) => void,
] {
  const pageBanner = useAppClientStore((s) => s.pageBanner);
  const setPageBanner = useCallback((updater: SetStateAction<boolean>) => {
    useAppClientStore.setState((state) => ({
      pageBanner: resolveUpdater(state.pageBanner, updater),
    }));
  }, []);
  return [pageBanner, setPageBanner];
}

export function usePageBannerValue(): boolean {
  return useAppClientStore((s) => s.pageBanner);
}

export function useRecentPropertySelections(): [
  RecentsStorageV2,
  (updater: SetStateAction<RecentsStorageV2>) => void,
] {
  const recentPropertySelections = useAppClientStore(
    (s) => s.recentPropertySelections
  );
  const setRecentPropertySelections = useCallback(
    (updater: SetStateAction<RecentsStorageV2>) => {
      useAppClientStore.setState((state) => ({
        recentPropertySelections: resolveUpdater(
          state.recentPropertySelections,
          updater
        ),
      }));
    },
    []
  );
  return [recentPropertySelections, setRecentPropertySelections];
}

export function useUserPropertyGroups(): [
  SavedGroupsStorageV2,
  (updater: SetStateAction<SavedGroupsStorageV2>) => void,
] {
  const userPropertyGroups = useAppClientStore((s) => s.userPropertyGroups);
  const setUserPropertyGroups = useCallback(
    (updater: SetStateAction<SavedGroupsStorageV2>) => {
      useAppClientStore.setState((state) => ({
        userPropertyGroups: resolveUpdater(state.userPropertyGroups, updater),
      }));
    },
    []
  );
  return [userPropertyGroups, setUserPropertyGroups];
}

export function useFavoriteProperties(): [
  string[],
  (updater: SetStateAction<string[]>) => void,
] {
  const favoriteProperties = useAppClientStore((s) => s.favoriteProperties);
  const setFavoriteProperties = useCallback(
    (updater: SetStateAction<string[]>) => {
      useAppClientStore.setState((state) => ({
        favoriteProperties: resolveUpdater(state.favoriteProperties, updater),
      }));
    },
    []
  );
  return [favoriteProperties, setFavoriteProperties];
}
